// @ts-strict-ignore
import isNumber from 'lodash/isNumber';
import isEqual from 'lodash/isEqual';
import mergeWith from 'lodash/mergeWith';
import memoizeLast from 'just-memoize-last';
import partition from 'lodash/partition';
import every from 'lodash/every';
import Highcharts from 'highcharts';

import {IChartDataItem, MarkerItem} from 'common/charts/visualization/line-chart/types';
import {
  AxisUnitPrecision,
  DisplayOptions,
  FlatSegment,
  InsightsBookmarkSections,
  InsightsChartParams,
  InsightsQueryResults,
  MetricMetadata,
  TypeOfComparison,
} from 'common/report/insights/util/types';
import {
  canShowOtherSegment,
  getDisplayOptions,
  getFlatSegments,
  getMissingZeroSegments,
  getResultsHeadersWithoutGroup,
  getSegmentLookupMap,
  getSeriesDisplaySegmentColumns,
  getSortedIsoDateKeysFromTimeseries,
  getSortedKeyMapForFlatSegments,
  getTimeseriesDates,
  getTimeseriesFromTimeComparisonValues,
  getTimestampTuplesFromTimeseries,
  getTrendDataRow,
  isPeopleTimeSeries,
  isSingleSegmentedMetric,
  lookupSegment,
  memoizedGetHighchartsTimestamp,
  memoizedGetTimestamp,
  shouldShowSeriesTotal,
} from 'common/report/insights/util';
import {ProjectTimezone} from 'common/report/stores/types';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {colors} from 'common/util/colors';
import {HIGHCHARTS_LINKED_ID_SUFFIX, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {
  ReadOnlySegmentItem,
  SegmentFilterController,
  SegmentItem,
} from 'common/charts/contexts/segment-filter-controller';
import {
  DisplayAnalysisType,
  DisplayPlotStyle,
  DisplayStackingType,
  LineStyle,
} from 'common/charts/visualization/line-chart/enums';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getTrendDataTableColumnGroups, getTrendDataTableRow} from 'common/components/data-table/trend-data/table';
import {Columns, CommonDataTableFields} from 'common/components/data-table/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {
  ClauseType,
  computeMetricClauseByKey,
  LineChartTooltipStat,
  RenderLineChartTooltipParams,
} from 'common/report/insights/util/tooltip';
import {getLiftBaselineSegment, getLiftComparisonValues} from 'common/report/util/lift';
import {LiftType} from 'common/report/util/lift/types';
import {ForecastValue} from 'common/report/types';
import {findShowClauseIndex} from 'common/report/insights/models/utils';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {
  getFinalDisplayOptions,
  getMetricFormattingDefaultsFromShowClause,
  getMetricKeyToShowClauseMap,
  metricFormatterFunctionForShowClause,
} from 'common/report/insights/charts/util';
import {ValueFormatterFunc} from 'common/components/data-table/trend-data/types';
import {formatMetricNumberWithUnits} from 'common/util';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {AnalysisType, ChartPlotStyle, ValueRepresentationType} from 'common/types/reports/bookmark';
import {LocalizationStore} from 'common/i18n/store';
import {getHighchartsColorString} from 'common/charts/visualization/line-chart/utils';

export interface LineChartData {
  lineChartSeries: IChartDataItem[]; // sorted array of line series for use in the visualization component
  legendSegmentItems: SegmentItem[]; // sorted array of info for each segment (for use in SegmentFilterController)
  readOnlyLegendItems: ReadOnlySegmentItem[];
  chartAnalysisType: DisplayAnalysisType;
  chartPlotStyle: DisplayPlotStyle;
  chartStackingType: DisplayStackingType;
  chartTimeUnit: TimeUnit;

  // Insights uses a special index with a stable sort order (independent from the bookmark sort order) to store selected legend segments
  legendIdToIndexMap: {[id: string]: number};
  legendIndexToId: string[];

  // easy lookups for data segment to
  segmentLookup: Map<string, FlatSegment>;
  timeComparisonSegmentLookup: Map<string, FlatSegment>;
  liftComparisonSegmentLookup: Map<string, FlatSegment>;
  forecastComparisonSegmentLookup: Map<string, FlatSegment<ForecastValue>>;

  tableColumns: Columns;
}

// convert Insights' display options to the corresponding analysis type used in <mp-line-chart>
function getLineChartAnalysisType(displayOptions: DisplayOptions): DisplayAnalysisType {
  if (displayOptions.analysis === AnalysisType.Logarithmic) {
    return DisplayAnalysisType.Logarithmic;
  }
  return DisplayAnalysisType.Linear;
}

// convert Insights' display options to the corresponding plot style used in <mp-line-chart>
function getLineChartPlotStyle(displayOptions: DisplayOptions): DisplayPlotStyle {
  if (displayOptions.plotStyle === ChartPlotStyle.Stacked) {
    return DisplayPlotStyle.Stacked;
  }
  return DisplayPlotStyle.Standard;
}

function getLineChartStackingType(
  displayOptions: DisplayOptions,
  sections: InsightsBookmarkSections,
  liftType?: LiftType,
): DisplayStackingType {
  if (displayOptions.plotStyle !== ChartPlotStyle.Stacked) {
    return undefined;
  }
  const isTotal = shouldShowSeriesTotal(sections);
  if (displayOptions.value === ValueRepresentationType.Relative || (liftType === LiftType.Overall && isTotal)) {
    return DisplayStackingType.Percent;
  }
  return DisplayStackingType.Normal;
}

export function getLineSeries(
  segment: FlatSegment,
  color: string,
  projectTimezone: ProjectTimezone,
  metricMetadata: MetricMetadata,
  isLinkedSeries?: boolean,
  axis?: BookmarkTypes.AxisAssignment,
): IChartDataItem {
  const name = segment.key;
  const id = name + (isLinkedSeries ? HIGHCHARTS_LINKED_ID_SUFFIX : ``);
  const incompleteLength = metricMetadata.incomplete_length;
  return {
    data: getTimestampTuplesFromTimeseries(segment.value, projectTimezone),
    axis,
    name,
    color,
    id,
    incompleteLength,
  };
}

export function getForecastSeries(
  segment: FlatSegment<ForecastValue>,
  color: string,
  projectTimezone: ProjectTimezone,
): IChartDataItem {
  const name = segment.key;
  const id = name + HIGHCHARTS_LINKED_ID_SUFFIX;
  const timestampTuples = getTimestampTuplesFromTimeseries(segment.value, projectTimezone);
  return {
    data: timestampTuples.map(([ds, forecastValue]) => {
      return [ds, forecastValue.yhat_lower, forecastValue.yhat_upper];
    }),
    name,
    color,
    id,
    linkedTo: name,
    incompleteLength: 0,
  };
}

function getAnomalyMarkers(mainSeries: IChartDataItem, forecastSeries: IChartDataItem): MarkerItem[] {
  const markers: MarkerItem[] = [];
  mainSeries.data.forEach((dataItem: any[]) => {
    const time = dataItem[0];
    const mainSeriesValue = dataItem[1];
    const forecastSeriesDataIndex = forecastSeries.data.findIndex((forecastDataItem) => forecastDataItem[0] === time);
    const forecastSeriesDataItem = forecastSeries.data[forecastSeriesDataIndex];
    const yhatLower = forecastSeriesDataItem[1];
    const yhatUpper = forecastSeriesDataItem[2];
    if (mainSeriesValue > yhatUpper || mainSeriesValue < yhatLower) {
      markers.push({linkedTo: mainSeries.name, color: colors.negative, data: dataItem});
    }
  });
  return markers;
}

function mergeSegmentValues(
  mainSegments: Map<string, FlatSegment>,
  timeComparisonSegments: Map<string, FlatSegment>,
  liftComparisonSegments: Map<string, FlatSegment>,
): FlatSegment[] {
  return Array.from(mainSegments.values(), (segment) => {
    if (timeComparisonSegments.has(segment.key)) {
      const timeComparisonSegment = timeComparisonSegments.get(segment.key);
      return {
        ...segment,
        value: {...segment.value, ...timeComparisonSegment.value},
      };
    }
    if (liftComparisonSegments.has(segment.key)) {
      const liftComparisonSegment = liftComparisonSegments.get(segment.key);
      return {
        ...segment,
        value: {...segment.value, ...liftComparisonSegment.value},
      };
    }
    return segment;
  });
}

function mergeValueRecords(values: Array<Record<string, number>>) {
  return values.reduce((acc, curr) => {
    return mergeWith({}, acc, curr, function (objValue, srcValue) {
      return isNumber(objValue) ? objValue + srcValue : srcValue;
    });
  }, {});
}

/**
 * Gets the `overall` segment for comparison and % of overall calculations. Works for both lift and non-lift cases:
 * If there exists an $overall segment, find and use it.
 * Otherwise, sum up all segments and use that as an overall proxy - it's not a "true" % of overall, but for things
 * that arb can't sum up in a true overall (i.e. multiple events instead of a breakdown), it is the current behavior
 * and still works for non-high cardinality queries
 * @param allSegments
 *
 * TODO(sam): remove/error the second part of this after lift is released - we should always have an overall segment
 */
function getOverallSegmentDataValues(allSegments: FlatSegment[]): Record<string, number> {
  const overallSegmentValues = allSegments.find((flatSegment) => flatSegment.path.includes(RESULTS_OVERALL_SUM_KEY));
  if (overallSegmentValues) {
    return overallSegmentValues.value;
  }

  return mergeValueRecords(allSegments.map((segment) => segment.value));
}

export interface OtherSegmentData {
  segment: FlatSegment;
  percentagesSegment: FlatSegment;
  metricKeys: string[];
  isAllZero: boolean;
}

/**
 * Returns an `OtherSegmentData` that has both percentage and value of all unselected values, used for both tooltips and line series
 * `segment` is used for raw data values for tooltips, while `percentagesSegment` is used for the line series to be rendered.
 * @param allSegments - All segments, selected, unselected, and overall
 * @param segmentFilterController - to determine which are selected/not
 */
export function getOtherSegmentData(
  allSegments: FlatSegment[],
  segmentFilterController: SegmentFilterController,
): OtherSegmentData {
  const [selectedSegments, unselectedSegments] = partition(
    allSegments.filter((segment) => !segment.path.includes(RESULTS_OVERALL_SUM_KEY)),
    (segment) => segmentFilterController.isItemSelected(0, segment.key),
  );

  const overallSegmentValues = getOverallSegmentDataValues(allSegments);

  const selectedSegmentValueDicts = selectedSegments.map((segment) => segment.value);
  const selectedSegmentValues = mergeValueRecords(selectedSegmentValueDicts);

  const overallMinusSelectedValues = mergeWith(
    {},
    overallSegmentValues,
    selectedSegmentValues,
    (destObj, subtractObj) => (isNumber(destObj) ? destObj - subtractObj : subtractObj),
  );

  const overallPercentageValues = mergeWith({}, overallMinusSelectedValues, overallSegmentValues, (destObj, divNum) =>
    // take the ratio and * 100 to get the actual percentage to use
    isNumber(destObj) ? (destObj / divNum) * 100 : divNum,
  );

  const isAllZero = every(overallMinusSelectedValues, (record) => record === 0);

  const segment: FlatSegment = {
    key: OTHER_SEGMENT_VALUE,
    path: [OTHER_SEGMENT_VALUE],
    displayName: `Other`,
    value: overallMinusSelectedValues,
  };

  const percentagesSegment = {
    ...segment,
    value: overallPercentageValues,
  };

  const metricKeys = unselectedSegments.map((unselectedSegment) => unselectedSegment.path[0]);
  return {segment, metricKeys, percentagesSegment, isAllZero};
}

function formatTooltipStatLogic<ValueT = number>(
  point: Highcharts.Point,
  segment: FlatSegment<ValueT>,
  projectTimezone: ProjectTimezone,
): LineChartTooltipStat<ValueT> {
  const dates = getSortedIsoDateKeysFromTimeseries(segment.value);
  const value: ValueT = segment.value[dates[point.index]];
  const secondaryValue: ValueT = segment.secondaryValue ? segment.secondaryValue[dates[point.index]] : null;

  const timestamp = memoizedGetTimestamp(dates[point.index]);
  const highchartsTimestamp = memoizedGetHighchartsTimestamp(
    dates[point.index],
    projectTimezone.timezone,
    projectTimezone.utcOffset,
  );

  return {
    highchartsTimestamp,
    secondaryValue,
    timestamp,
    value,
  };
}

function formatTooltipStat(
  point: Highcharts.Point,
  segment: FlatSegment,
  projectTimezone: ProjectTimezone,
): LineChartTooltipStat {
  const stat = formatTooltipStatLogic(point, segment, projectTimezone);
  stat.value = stat.value ?? 0;
  if (stat.secondaryValue) {
    stat.secondaryValue = stat.secondaryValue ?? 0;
  }
  return stat;
}

function formatTooltipForecastValueStat(
  lineChartData: LineChartData,
  point: Highcharts.Point,
  segment: FlatSegment<ForecastValue>,
  projectTimezone: ProjectTimezone,
): LineChartTooltipStat<ForecastValue> {
  // for charts with forecast comparisons, find the true index for the point first since
  // anomaly marker points don't map 1 to 1 on the main series
  const forecastPointIndex = lineChartData.lineChartSeries[0].data.findIndex((dataItem) => dataItem[0] === point.x);
  const forecastPoint = {...point, index: forecastPointIndex} as Highcharts.Point;
  const stat = formatTooltipStatLogic<ForecastValue>(forecastPoint, segment, projectTimezone);
  stat.value = stat.value ?? {yhat_lower: 0, yhat_upper: 0};
  return stat;
}

export function generatePointTooltipParams({
  highchartsContext,
  bookmarkParams,
  results,
  lineChartData,
  dataGroups,
  projectTimezone,
  otherSegmentData,
  metrics,
  i18n,
}: {
  highchartsContext: {
    point: Highcharts.Point;
    series: Highcharts.Series;
    percentage: number;
  };
  bookmarkParams: BookmarkTypes.InsightsBookmarkParams;
  results: InsightsQueryResults;
  lineChartData: LineChartData;
  dataGroups: DataGroup[];
  projectTimezone: ProjectTimezone;
  otherSegmentData?: OtherSegmentData;
  metrics: MetricsResource;
  i18n: LocalizationStore;
}): RenderLineChartTooltipParams {
  const {point, series, percentage} = highchartsContext;
  const segmentKey = series.options.name; // use name from options because highcharts randomly names series with an empty string name
  const showPreviousValueDelta = !Number.isFinite(percentage) && point.index > 0;
  const {segmentLookup} = lineChartData;
  const reportSectionsMetadata = results.meta.report_sections;
  const metricClauseByKey = computeMetricClauseByKey({
    sectionsMetadata: reportSectionsMetadata,
    bookmarkParams,
    dataGroups,
  });

  let mainSegment: FlatSegment = segmentLookup.get(segmentKey);
  let metricClauseData = metricClauseByKey[mainSegment?.path?.[0]];
  if (segmentKey === OTHER_SEGMENT_VALUE) {
    mainSegment = otherSegmentData.segment;
    metricClauseData = {
      clauseType: ClauseType.Other,
      metricClauses: otherSegmentData.metricKeys.map((key) => metricClauseByKey[key]),
      metricKey: OTHER_SEGMENT_VALUE,
    };
  }
  const metricMetadata = reportSectionsMetadata.show.find((metadata) => {
    return metadata.metric_key === mainSegment?.path?.[0];
  });

  const mainDates = getSortedIsoDateKeysFromTimeseries(mainSegment.value);
  const deltaStat: LineChartTooltipStat = showPreviousValueDelta
    ? {
        value: mainSegment.value[mainDates[point.index - 1]],
      }
    : null;

  const stat: LineChartTooltipStat = {
    ...formatTooltipStat(point, mainSegment, projectTimezone),
    dateRange: {
      fromDate: results.date_range.from_date,
      toDate: results.date_range.to_date,
    },
    segmentKey: `arbitrary`,
  };

  let timeComparisonStat: null | LineChartTooltipStat = null;
  let liftComparisonStat: null | LineChartTooltipStat = null;
  let baselineComparisonStat: null | LineChartTooltipStat = null;
  let overallComparisonStat: null | LineChartTooltipStat = null;
  let forecastComparisonStat: null | LineChartTooltipStat<ForecastValue> = null;

  if (lineChartData.liftComparisonSegmentLookup.has(segmentKey)) {
    const liftComparisonSegment = lookupSegment(
      segmentKey,
      lineChartData.liftComparisonSegmentLookup,
      mainSegment.path,
    );

    liftComparisonStat = {
      ...formatTooltipStat(point, liftComparisonSegment, projectTimezone),
      // we only want to display the leaf segment name
      segmentKey: segmentKey.split(`,`).slice(-1)[0],
    };

    const {type}: {type: LiftType} = results.meta.liftComparison;

    switch (type) {
      case LiftType.Time:
        // Time will be calculated with the timeComparisonSegmentLookup
        break;
      case LiftType.Overall:
        overallComparisonStat = {
          ...formatTooltipStat(
            point,
            lookupSegment(RESULTS_OVERALL_SUM_KEY, segmentLookup, mainSegment.path),
            projectTimezone,
          ),
          segmentKey: `Overall`,
        };
        break;
      case LiftType.Segment: {
        const baselineSegmentKey = [
          ...segmentKey.split(`,`).slice(0, -1),
          results.meta.liftComparison.groupValue || results.meta.liftComparison.cohort.name,
        ].join(`,`);
        if (baselineSegmentKey) {
          baselineComparisonStat = {
            ...formatTooltipStat(
              point,
              lookupSegment(baselineSegmentKey, segmentLookup, mainSegment.path),
              projectTimezone,
            ),
            segmentKey: baselineSegmentKey,
          };
        }
        break;
      }
    }
  }

  if (lineChartData.timeComparisonSegmentLookup.has(segmentKey)) {
    const comparisonSegment = lookupSegment(segmentKey, lineChartData.timeComparisonSegmentLookup);
    timeComparisonStat = {
      ...formatTooltipStat(point, comparisonSegment, projectTimezone),
      dateRange: {
        fromDate: results.time_comparison.date_range.from_date,
        toDate: results.time_comparison.date_range.to_date,
      },
    };
  }

  if (lineChartData.forecastComparisonSegmentLookup.has(segmentKey)) {
    const forecastSegment = lookupSegment<ForecastValue>(segmentKey, lineChartData.forecastComparisonSegmentLookup);
    forecastComparisonStat = formatTooltipForecastValueStat(lineChartData, point, forecastSegment, projectTimezone);
    // for charts with forecast comparisons, use the point values for the stat since
    // anomaly marker points don't map 1 to 1 on the main series
    stat.value = point.y;
    stat.highchartsTimestamp = point.x;
  }

  const displayOptions = getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions);
  const isFirstInterval = point.index === 0;
  const isLastInterval = point.index === series.data.length - 1;
  const isIncomplete = series.data.length - point.index <= metricMetadata?.incomplete_length;
  const colorString = getHighchartsColorString(series.color);

  return {
    headers: results.headers,
    metricClauseData,
    metricsResource: metrics,
    // this populates the breakdown subheading, exclude the primary metric in the segment array
    segments: mainSegment.path.slice(1),
    stat,
    deltaStat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    overallComparisonStat,
    forecastComparisonStat,
    percent: percentage,
    timeUnit: displayOptions.timeUnit,
    isFirstInterval,
    isLastInterval,
    isIncomplete,
    titleColor: colorString,
    timezone: projectTimezone.timezone,
    utcOffset: projectTimezone.utcOffset,
    // @ts-ignore Property 'isComparisonItem' does not exist on type 'SeriesOptionsType'.
    isComparisonItem: series.userOptions.isComparisonItem,
    i18n,
  };
}

export interface LegendSegmentTooltipParams {
  metricName: string;
  path: string[];
  value: string;
  color: string;
  aggregate: number;
}

export function _getLineChartData(
  projectTimezone: ProjectTimezone,
  chartParams: InsightsChartParams,
  originalResults: InsightsQueryResults,
  dataGroups: Record<string, DataGroup>,
  metricsResource: MetricsResource,
  i18n: LocalizationStore,
): LineChartData {
  // When displayOptions.chartType is "line", the last breakdown-by-date in a people query isn't formatted like a normal breakdown in query results,
  // query-API will actually format it like a timeseries. That's why we omit the last breakdown from group clauses and headers in this special case.
  const shouldOmitLastGroup = isPeopleTimeSeries(chartParams.sections);
  const bookmarkSections = shouldOmitLastGroup
    ? {...chartParams.sections, group: chartParams.sections.group.slice(0, -1)}
    : chartParams.sections;
  const results = shouldOmitLastGroup
    ? {
        ...originalResults,
        headers: getResultsHeadersWithoutGroup(
          originalResults.headers,
          chartParams.sections.group[chartParams.sections.group.length - 1],
        ),
      }
    : originalResults;

  const showTimeComparison =
    !!results.time_comparison && chartParams.displayOptions.plotStyle !== ChartPlotStyle.Stacked;

  // flatten results into convenient segments
  const segments = getFlatSegments({
    headers: results.headers,
    resultSeries: results.series,
    groupClauses: chartParams.sections.group,
    secondaryResultSeries: results.secondary?.series,
  });
  let timeComparisonSegments: FlatSegment[] = [];
  const liftComparisonSegments: FlatSegment[] = results?.lift_comparison
    ? getFlatSegments({
        headers: results.headers,
        resultSeries: results.lift_comparison.series,
        groupClauses: chartParams.sections.group,
      })
    : [];
  let forecastComparisonSegments: Array<FlatSegment<ForecastValue>> = [];

  const segmentColumns = getSeriesDisplaySegmentColumns(bookmarkSections, results, dataGroups, i18n);

  // Insights API results typically omit segments that have a value of zero for the whole date range
  // which means `results.series` & `results.time_comparison.series` can contain a differing number of segments.
  // Resurrect these omitted segments as zero-value segments so both series match again.
  if (showTimeComparison) {
    if (results?.time_comparison) {
      timeComparisonSegments = getFlatSegments({
        headers: results.headers,
        resultSeries: results.time_comparison.series,
        groupClauses: chartParams.sections.group,
        secondaryResultSeries: results.secondary_time_comparison?.series,
      });
      const timeseriesDates = getTimeseriesDates(results, TypeOfComparison.Time);

      segments.push(...getMissingZeroSegments(timeComparisonSegments, segments, timeseriesDates.main));
      timeComparisonSegments.push(
        ...getMissingZeroSegments(segments, timeComparisonSegments, timeseriesDates.timeComparison),
      );
    }

    if (results?.lift_comparison) {
      const timeseriesDates = getTimeseriesDates(results, TypeOfComparison.Lift);

      segments.push(...getMissingZeroSegments(liftComparisonSegments, segments, timeseriesDates.main));
      liftComparisonSegments.push(
        ...getMissingZeroSegments(segments, liftComparisonSegments, timeseriesDates.timeComparison),
      );
    }
  }

  if (results.forecast_comparison) {
    forecastComparisonSegments = getFlatSegments({
      headers: results.headers,
      resultSeries: results.forecast_comparison.series,
      groupClauses: chartParams.sections.group,
    });
  }

  // transform values according to bookmark display options
  const displayOptions = getDisplayOptions(bookmarkSections, chartParams.displayOptions);

  // create lookups based on segment key, useful for correlating a main segment with a corresponding time comparison segment
  const segmentLookup = getSegmentLookupMap(segments);
  const timeComparisonSegmentLookup = getSegmentLookupMap(timeComparisonSegments);
  const liftComparisonSegmentLookup = getSegmentLookupMap(liftComparisonSegments);
  const forecastComparisonSegmentLookup = getSegmentLookupMap(forecastComparisonSegments);

  // merge segment values to include time comparison values when sorting
  const mergedValueSegments = mergeSegmentValues(
    segmentLookup,
    timeComparisonSegmentLookup,
    liftComparisonSegmentLookup,
  );

  const segmentKeys = mergedValueSegments.filter((s) => !s.path.includes(RESULTS_OVERALL_SUM_KEY)).map((s) => s.key);

  // generate line chart series & legend info from segments
  const legendSegmentItems: SegmentItem[] = [];
  let tableColumns: Columns;

  const _isSingleSegmentedMetric = isSingleSegmentedMetric(results.headers, results.series);

  segmentKeys.forEach((segmentKey) => {
    const segment = segmentLookup.get(segmentKey);
    const liftComparisonSegment = liftComparisonSegmentLookup.get(segmentKey);
    const timeComparisonSegment = timeComparisonSegmentLookup.get(segmentKey);

    // improves readability for the legend when displaying results for single segmented metrics
    const segmentItem = {
      id: segmentKey,
      label: _isSingleSegmentedMetric ? segment.displayNamePath.slice(1).join(` / `) : segment.displayName,
    };

    const trendDataRow = getTrendDataRow({
      isSelected: false,
      segmentItem,
      segment,
      timeComparisonSegment,
      liftComparisonSegment,
      projectTimezone,
    });

    if (!tableColumns) {
      const metricKeyToShowSection = getMetricKeyToShowClauseMap(chartParams, results);

      const valueFormatter: ValueFormatterFunc = (item, context, options = {}) => {
        const inputValue = options.isLift ? item[context.liftExtra] : item[context.field];
        const showClause = metricKeyToShowSection[item[CommonDataTableFields.SegmentPrefix + `0`]];
        const formattedValue = metricFormatterFunctionForShowClause(showClause)(inputValue);
        const defaultOptions = {
          ...getMetricFormattingDefaultsFromShowClause(showClause),
          // For average column, show 1 decimal place even if the value is an integer.
          ...(options.isAverage ? {precision: 1 as AxisUnitPrecision} : {}),
        };

        return formatMetricNumberWithUnits(
          formattedValue,
          getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metricsResource, defaultOptions),
        );
      };
      tableColumns = getTrendDataTableColumnGroups({
        groupClauses: chartParams.sections.group,
        liftType: chartParams.liftComparison?.type,
        row: trendDataRow,
        segments: segmentColumns,
        timezone: projectTimezone.timezone,
        trendUnit: displayOptions.timeUnit,
        valueFormatter,
      });
    }

    legendSegmentItems.push({
      ...segmentItem,
      tableRow: getTrendDataTableRow(
        trendDataRow,
        getLiftComparisonValues({
          segment,
          comparisonSegment: getLiftBaselineSegment(
            chartParams.liftComparison,
            segment.path,
            segmentLookup,
            timeComparisonSegment,
          ),
          liftType: chartParams.liftComparison?.type,
          projectTimezone,
        }),
      ),
    });
  });
  tableColumns = tableColumns || [];

  const lineChartSeries: IChartDataItem[] = [];
  legendSegmentItems.forEach((segmentItem, idx) => {
    const segmentKey = segmentItem.id;
    const segment = segmentLookup.get(segmentKey);
    const metricMetadata = results.meta.report_sections.show[0];
    const showIdx = findShowClauseIndex(results.meta.report_sections.show, segment.path);
    const axis = chartParams.sections.show[showIdx]?.display?.axis;
    const mainSeries = getLineSeries(
      segment,
      getChartSegmentColorCombo(idx).primary,
      projectTimezone,
      metricMetadata,
      false,
      axis,
    );
    const liftComparisonSegment = liftComparisonSegmentLookup.get(segmentKey);
    const timeComparisonSegment = timeComparisonSegmentLookup.get(segmentKey);
    const forecastComparisonSegment = forecastComparisonSegmentLookup.get(segmentKey);

    if (liftComparisonSegment) {
      const compositeLiftSeries = getTimeseriesFromTimeComparisonValues(
        segment.value,
        liftComparisonSegment.value,
        // Delegate formatting as a percent to formatting utils so they can
        // properly take into account all cases.
        false,
      );
      const compositeSegment: FlatSegment = {...liftComparisonSegment, value: compositeLiftSeries};
      lineChartSeries.push({
        ...getLineSeries(
          compositeSegment,
          getChartSegmentColorCombo(idx).primary,
          projectTimezone,
          metricMetadata,
          false,
          axis,
        ),
        isComparisonItem: true,
      });
    } else {
      lineChartSeries.push(mainSeries);

      if (timeComparisonSegment) {
        const compositeTimeseries = getTimeseriesFromTimeComparisonValues(
          segment.value,
          timeComparisonSegment.value,
          false,
        );
        const compositeSegment: FlatSegment = {...timeComparisonSegment, value: compositeTimeseries};
        lineChartSeries.push({
          ...getLineSeries(
            compositeSegment,
            getChartSegmentColorCombo(idx).primary,
            projectTimezone,
            metricMetadata,
            true,
            axis,
          ),
          dashStyle: LineStyle.Comparison,
          incompleteLength: 0,
          isComparisonItem: true,
        });
      }

      if (forecastComparisonSegment) {
        const forecastSeries = getForecastSeries(
          forecastComparisonSegment,
          getChartSegmentColorCombo(idx).primary,
          projectTimezone,
        );
        lineChartSeries.push(forecastSeries);
        const anomalyMarkers = getAnomalyMarkers(mainSeries, forecastSeries);
        if (anomalyMarkers?.length > 0) {
          mainSeries.markers = anomalyMarkers;
        }
      }
    }
  });

  let readOnlyLegendItems = [];
  if (canShowOtherSegment(chartParams)) {
    readOnlyLegendItems = [
      {
        id: OTHER_SEGMENT_VALUE,
        label: `Other`,
        color: `none`,
      },
    ];
  }

  const legendIdToIndexMap = getSortedKeyMapForFlatSegments(segments);
  const legendIndexToId: string[] = [];
  Object.keys(legendIdToIndexMap).forEach((id) => (legendIndexToId[legendIdToIndexMap[id]] = id));

  // visualization component display options
  const chartAnalysisType = getLineChartAnalysisType(displayOptions);
  const chartPlotStyle = getLineChartPlotStyle(displayOptions);
  const chartStackingType = getLineChartStackingType(
    displayOptions,
    chartParams.sections,
    chartParams.liftComparison?.type,
  );
  const chartTimeUnit = displayOptions.timeUnit ?? TimeUnit.Day;

  return {
    lineChartSeries,
    legendSegmentItems,
    readOnlyLegendItems,
    chartAnalysisType,
    chartPlotStyle,
    chartStackingType,
    segmentLookup,
    timeComparisonSegmentLookup,
    liftComparisonSegmentLookup,
    forecastComparisonSegmentLookup,
    chartTimeUnit,
    legendIdToIndexMap,
    legendIndexToId,
    tableColumns,
  };
}
export const getLineChartData: (
  projectTimezone: ProjectTimezone,
  chartParams: InsightsChartParams,
  originalResults: InsightsQueryResults,
  dataGroups: Record<string, DataGroup>,
  metricsResource: MetricsResource,
  i18n: LocalizationStore,
) => LineChartData = memoizeLast(_getLineChartData, isEqual);
